/**
 * SlabEditorDialog — admin-only editor for the incentive slab configuration.
 *
 * Wired from IncentiveReportPage.js next to "How the incentive is calculated".
 * Renders an editable table of slabs (low / high / incentive / label), plus
 * inputs for the two gate %s and the adjustment window. Save → PUT
 * /api/reports/incentive/config; parent refetches config + summary so the
 * updated brackets reflow into the calculations immediately.
 */
import { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Plus, Trash2, Loader2, ArrowUp, ArrowDown, IndianRupee, History, Calendar, ChevronDown, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';

const emptyRow = () => ({ low: 0, high: null, incentive: 0, label: '' });
const todayISO = () => new Date().toISOString().slice(0, 10);

export default function SlabEditorDialog({ open, onClose, initialConfig, onSaved }) {
  const [rows, setRows] = useState([]);
  const [achievement, setAchievement] = useState(65);
  const [payment, setPayment] = useState(60);
  const [window, setWindow] = useState(60);
  const [effectiveFrom, setEffectiveFrom] = useState(todayISO());
  const [saving, setSaving] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [expanded, setExpanded] = useState({});

  useEffect(() => {
    if (!open || !initialConfig) return;
    setRows((initialConfig.brackets || []).map(b => ({
      low: b.low ?? 0,
      high: b.high, // may be null (open-ended top slab)
      incentive: b.incentive ?? 0,
      label: b.label ?? '',
    })));
    setAchievement(initialConfig.achievement_gate_pct ?? 65);
    setPayment(initialConfig.payment_gate_pct ?? 60);
    setWindow(initialConfig.adjustment_window_days ?? 60);
    setEffectiveFrom(todayISO());
  }, [open, initialConfig]);

  const loadHistory = async () => {
    setHistoryLoading(true);
    try {
      const r = await api.get('/reports/incentive/config/history');
      setHistory(r.data.versions || []);
    } catch { /* silent */ } finally { setHistoryLoading(false); }
  };
  useEffect(() => { if (open) loadHistory(); }, [open]);

  const patch = (i, key, v) => setRows(r => r.map((row, idx) => idx === i ? { ...row, [key]: v } : row));
  const addRow = () => {
    // Insert after the last row; auto-fill low as prev high, high blank
    setRows(r => {
      if (r.length === 0) return [emptyRow()];
      const last = r[r.length - 1];
      const newLow = last.high ?? Number(last.low) + 25000;
      return [...r, { low: newLow, high: null, incentive: 0, label: '' }];
    });
  };
  const remove = (i) => setRows(r => r.filter((_, idx) => idx !== i));
  const move = (i, delta) => setRows(r => {
    const j = i + delta;
    if (j < 0 || j >= r.length) return r;
    const copy = [...r];
    [copy[i], copy[j]] = [copy[j], copy[i]];
    return copy;
  });

  const save = async () => {
    // Client-side validation mirrors the backend for a snappy UX
    if (rows.length === 0) { toast.error('At least one slab is required'); return; }
    const sorted = [...rows].sort((a, b) => Number(a.low || 0) - Number(b.low || 0));
    for (let i = 0; i < sorted.length; i++) {
      const s = sorted[i];
      const lo = Number(s.low || 0);
      const hi = (s.high === null || s.high === '' || s.high === undefined) ? null : Number(s.high);
      if (Number.isNaN(lo) || lo < 0) { toast.error(`Slab ${i + 1}: invalid "From" amount`); return; }
      if (hi !== null && (Number.isNaN(hi) || hi <= lo)) { toast.error(`Slab ${i + 1}: "To" must be greater than "From" (or blank for open-ended)`); return; }
      if (Number(s.incentive || 0) < 0) { toast.error(`Slab ${i + 1}: incentive cannot be negative`); return; }
      if (i + 1 < sorted.length && hi !== null && Number(sorted[i + 1].low || 0) < hi) {
        toast.error(`Slab ${i + 1} overlaps slab ${i + 2}`); return;
      }
    }
    setSaving(true);
    try {
      const payload = {
        brackets: sorted.map(s => ({
          low: Number(s.low),
          high: (s.high === null || s.high === '' || s.high === undefined) ? null : Number(s.high),
          incentive: Number(s.incentive || 0),
          label: (s.label || '').trim() || null,
        })),
        achievement_gate_pct: Number(achievement),
        payment_gate_pct: Number(payment),
        adjustment_window_days: Number(window),
        effective_from: effectiveFrom,
      };
      await api.put('/reports/incentive/config', payload);
      toast.success('Slab configuration saved');
      loadHistory();
      onSaved?.();
      onClose?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose?.(); }}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="slab-editor-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <IndianRupee className="w-4 h-4 text-indigo-600" />
            Edit Incentive Slabs
          </DialogTitle>
          <p className="text-[11px] text-slate-500 mt-1">Add or remove slabs and adjust gate percentages. Changes apply immediately to every user&apos;s incentive calculation.</p>
        </DialogHeader>

        {/* Slabs table */}
        <div className="border border-slate-200 rounded-md overflow-hidden">
          <div className="grid grid-cols-[1fr_1fr_1fr_2fr_auto] gap-2 px-3 py-2 bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500 font-medium">
            <div>From (₹)</div>
            <div>To (₹, blank = open)</div>
            <div>Incentive (₹)</div>
            <div>Label (optional)</div>
            <div />
          </div>
          {rows.map((r, i) => (
            <div key={i} className="grid grid-cols-[1fr_1fr_1fr_2fr_auto] gap-2 px-3 py-1.5 border-t border-slate-100 items-center" data-testid={`slab-row-${i}`}>
              <Input
                type="number"
                value={r.low ?? ''}
                onChange={e => patch(i, 'low', e.target.value)}
                className="h-8 text-xs"
                data-testid={`slab-low-${i}`}
              />
              <Input
                type="number"
                value={r.high ?? ''}
                onChange={e => patch(i, 'high', e.target.value === '' ? null : e.target.value)}
                placeholder="∞"
                className="h-8 text-xs"
                data-testid={`slab-high-${i}`}
              />
              <Input
                type="number"
                value={r.incentive ?? ''}
                onChange={e => patch(i, 'incentive', e.target.value)}
                className="h-8 text-xs"
                data-testid={`slab-incentive-${i}`}
              />
              <Input
                value={r.label || ''}
                onChange={e => patch(i, 'label', e.target.value)}
                placeholder="Auto-generated if blank"
                className="h-8 text-xs"
                data-testid={`slab-label-${i}`}
              />
              <div className="flex items-center gap-0.5">
                <Button size="icon" variant="ghost" className="h-7 w-7 text-slate-400 hover:text-slate-700" onClick={() => move(i, -1)} disabled={i === 0} data-testid={`slab-up-${i}`}><ArrowUp className="w-3 h-3" /></Button>
                <Button size="icon" variant="ghost" className="h-7 w-7 text-slate-400 hover:text-slate-700" onClick={() => move(i, 1)} disabled={i === rows.length - 1} data-testid={`slab-down-${i}`}><ArrowDown className="w-3 h-3" /></Button>
                <Button size="icon" variant="ghost" className="h-7 w-7 text-rose-500 hover:text-rose-700" onClick={() => remove(i)} disabled={rows.length <= 1} data-testid={`slab-remove-${i}`}><Trash2 className="w-3 h-3" /></Button>
              </div>
            </div>
          ))}
          <div className="px-3 py-2 border-t border-slate-100 bg-slate-50">
            <Button size="sm" variant="outline" onClick={addRow} className="h-7 text-xs" data-testid="slab-add-row">
              <Plus className="w-3 h-3 mr-1" /> Add slab
            </Button>
          </div>
        </div>

        {/* Gates + window + effective-from */}
        <div className="grid grid-cols-4 gap-3 mt-3">
          <div>
            <Label htmlFor="achievement-pct" className="text-[11px] text-slate-600">Achievement Gate %</Label>
            <Input id="achievement-pct" type="number" value={achievement} onChange={e => setAchievement(e.target.value)} className="h-8 text-xs mt-1" data-testid="slab-achievement-pct" />
            <p className="text-[10px] text-slate-500 mt-1">Achieved &gt; this % of target</p>
          </div>
          <div>
            <Label htmlFor="payment-pct" className="text-[11px] text-slate-600">Payment Gate %</Label>
            <Input id="payment-pct" type="number" value={payment} onChange={e => setPayment(e.target.value)} className="h-8 text-xs mt-1" data-testid="slab-payment-pct" />
            <p className="text-[10px] text-slate-500 mt-1">Payment received &gt; this %</p>
          </div>
          <div>
            <Label htmlFor="adj-window" className="text-[11px] text-slate-600">Adjustment window (days)</Label>
            <Input id="adj-window" type="number" value={window} onChange={e => setWindow(e.target.value)} className="h-8 text-xs mt-1" data-testid="slab-adjustment-window" />
            <p className="text-[10px] text-slate-500 mt-1">T+N days before the row locks</p>
          </div>
          <div>
            <Label htmlFor="eff-from" className="text-[11px] text-slate-600 flex items-center gap-1"><Calendar className="w-3 h-3" />Effective from</Label>
            <Input id="eff-from" type="date" value={effectiveFrom} onChange={e => setEffectiveFrom(e.target.value)} className="h-8 text-xs mt-1" data-testid="slab-effective-from" />
            <p className="text-[10px] text-slate-500 mt-1">Applies to months on or after</p>
          </div>
        </div>

        {/* Activity History — collapsible; each version = one entry with diff */}
        <div className="mt-3 border border-slate-200 rounded-md overflow-hidden bg-slate-50/40">
          <button
            type="button"
            onClick={() => setHistoryOpen(o => !o)}
            className="w-full flex items-center justify-between px-3 py-2 text-[11px] font-semibold text-slate-700 hover:bg-slate-100 transition"
            data-testid="slab-history-toggle"
          >
            <span className="flex items-center gap-1.5">
              <History className="w-3.5 h-3.5 text-indigo-600" />
              Activity History {history.length > 0 && <span className="text-[10px] font-normal text-slate-500">· {history.length} version{history.length === 1 ? '' : 's'}</span>}
            </span>
            {historyOpen ? <ChevronDown className="w-3.5 h-3.5 text-slate-400" /> : <ChevronRight className="w-3.5 h-3.5 text-slate-400" />}
          </button>
          {historyOpen && (
            <div className="border-t border-slate-200 bg-white max-h-64 overflow-y-auto" data-testid="slab-history-body">
              {historyLoading ? (
                <p className="text-[11px] text-slate-400 italic py-4 text-center"><Loader2 className="w-3 h-3 animate-spin inline mr-1" />Loading…</p>
              ) : history.length === 0 ? (
                <p className="text-[11px] text-slate-400 italic py-4 text-center">No history yet.</p>
              ) : history.map((v, i) => {
                const isOpen = !!expanded[v.id];
                const rangeLabel = v.effective_to ? `${v.effective_from} → ${v.effective_to}` : `${v.effective_from} → active`;
                const isActive = !v.effective_to;
                return (
                  <div key={v.id || i} className="border-b border-slate-100 last:border-b-0" data-testid={`slab-history-entry-${i}`}>
                    <button
                      type="button"
                      onClick={() => setExpanded(p => ({ ...p, [v.id]: !p[v.id] }))}
                      className="w-full flex items-start gap-2 px-3 py-2 text-left hover:bg-indigo-50/30 transition"
                    >
                      {isOpen ? <ChevronDown className="w-3 h-3 text-slate-400 mt-0.5 flex-shrink-0" /> : <ChevronRight className="w-3 h-3 text-slate-400 mt-0.5 flex-shrink-0" />}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-[11px] font-semibold text-slate-800 tabular-nums">{rangeLabel}</span>
                          {isActive && <span className="text-[9px] font-semibold px-1 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200">ACTIVE</span>}
                          <span className="text-[10px] text-slate-500">by {v.created_by || 'unknown'}</span>
                          {v.created_at && <span className="text-[10px] text-slate-400">· {new Date(v.created_at).toLocaleString()}</span>}
                        </div>
                        <p className="text-[11px] text-slate-600 mt-0.5 truncate">
                          {(v.changes || []).slice(0, 2).join(' · ')}
                          {(v.changes || []).length > 2 ? ` · +${v.changes.length - 2} more` : ''}
                        </p>
                      </div>
                    </button>
                    {isOpen && (
                      <div className="px-6 pb-2 -mt-1 space-y-1">
                        <ul className="list-disc pl-4 text-[11px] text-slate-600 leading-relaxed">
                          {(v.changes || []).map((c, ci) => <li key={ci}>{c}</li>)}
                        </ul>
                        <div className="mt-1.5 flex flex-wrap gap-1">
                          {(v.brackets || []).map((b, bi) => (
                            <span key={bi} className="text-[9px] font-medium px-1.5 py-0.5 rounded border border-slate-200 bg-white text-slate-700 tabular-nums">
                              {b.label || (b.high == null ? `≥ ₹${Number(b.low).toLocaleString('en-IN')}` : `₹${Number(b.low).toLocaleString('en-IN')}–<₹${Number(b.high).toLocaleString('en-IN')}`)}: ₹{Number(b.incentive || 0).toLocaleString('en-IN')}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <DialogFooter className="mt-3">
          <Button size="sm" variant="outline" onClick={onClose} data-testid="slab-cancel">Cancel</Button>
          <Button size="sm" onClick={save} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="slab-save">
            {saving ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : null}
            Save Configuration
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
